<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import { toast } from 'vue3-toastify';
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue';
import SubjectOfBusinessCategoryDataService from '@/services/SubjectOfBusinessCategoryDataService';
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table';
import { TableCard, TableSearch, TableEmptyState } from '@/components/common/table';

interface Category {
  id: number;
  title: string;
  parent_id?: number | null;
}

const categories = ref<Category[]>([]);
const search = ref('');
const showModal = ref(false);
const editMode = ref(false);
const form = ref<{ id: number | null; title: string; parent_id: number | null }>({ id: null, title: '', parent_id: null });
const sort = reactive({ key: 'id', asc: true });

function resetSearch() {
  search.value = '';
}

function sortBy(key: keyof Category) {
  if (sort.key === key) {
    sort.asc = !sort.asc;
  } else {
    sort.key = key;
    sort.asc = true;
  }
}

const filteredCategories = computed(() => {
  if (!search.value) return categories.value;
  const s = search.value.toLowerCase();
  return categories.value.filter((item) =>
    (item.title && item.title.toLowerCase().includes(s)) ||
    (item.id && String(item.id).includes(s))
  );
});

const sortedCategories = computed(() => {
  const arr = [...filteredCategories.value];
  arr.sort((a, b) => {
    let aVal = a[sort.key as keyof Category];
    let bVal = b[sort.key as keyof Category];
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sort.asc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sort.asc ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0) : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
  });
  return arr;
});

function getParentTitle(parent_id: number | null) {
  if (!parent_id) return 'Žiadna';
  const parent = categories.value.find((cat) => cat.id === parent_id);
  return parent ? parent.title : 'Žiadna';
}

function openAddModal() {
  editMode.value = false;
  form.value = { id: null, title: '', parent_id: null };
  showModal.value = true;
}

function openEditModal(cat: Category) {
  editMode.value = true;
  form.value = { id: cat.id, title: cat.title, parent_id: cat.parent_id ?? null };
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

async function fetchData() {
  const res = await SubjectOfBusinessCategoryDataService.getAllCategories();
  categories.value = res.data.data;
}

function extractErrorMessage(error: unknown, prefix = ''): string {
  const err = error as any;
  if (
    err &&
    typeof err === 'object' &&
    'response' in err &&
    err.response &&
    typeof err.response === 'object' &&
    'data' in err.response &&
    err.response.data &&
    typeof err.response.data === 'object' &&
    'message' in err.response.data &&
    typeof err.response.data.message === 'string'
  ) {
    return prefix + err.response.data.message;
  } else if (error instanceof Error) {
    return prefix + error.message;
  } else {
    return prefix + String(error);
  }
}

async function addCategory() {
  const { id, ...payload } = form.value;
  if (!payload.parent_id) payload.parent_id = null;
  try {
    await SubjectOfBusinessCategoryDataService.add(payload);
    await fetchData();
    closeModal();
    toast.success('Kategória bola úspešne pridaná.');
  } catch (error) {
    toast.error(extractErrorMessage(error, 'Chyba pri pridávaní kategórie: '));
  }
}

async function updateCategory() {
  const payload = { title: form.value.title, parent_id: form.value.parent_id };
  if (!payload.parent_id) payload.parent_id = null;
  try {
    await SubjectOfBusinessCategoryDataService.update(form.value.id, payload);
    await fetchData();
    closeModal();
    toast.success('Kategória bola úspešne upravená.');
  } catch (error) {
    toast.error(extractErrorMessage(error, 'Chyba pri úprave kategórie: '));
  }
}

async function deleteCategory(id: number) {
  if (confirm('Naozaj chcete vymazať túto kategóriu?')) {
    try {
      await SubjectOfBusinessCategoryDataService.delete(id);
      await fetchData();
      toast.success('Kategória bola úspešne vymazaná.');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Chyba pri mazaní kategórie: '));
    }
  }
}

onMounted(fetchData);
</script>

<template>
  <div class="p-6">
    <PageBreadcrumb page-title="Správa kategórií predmetov podnikania" />
    <TableCard title="Kategórie predmetov podnikania">
      <template #controls>
        <div class="flex w-full items-center gap-3 sm:w-auto">
          <TableSearch v-model="search" placeholder="Hľadať..." />
          <button
            v-if="search"
            type="button"
            class="shrink-0 px-4 py-2 bg-gray-200 text-gray-700 rounded-md dark:bg-gray-800 dark:text-gray-300"
            @click="resetSearch"
          >
            Reset
          </button>
          <button
            type="button"
            class="shrink-0 px-4 py-2 bg-brand-600 text-white rounded-md"
            @click="openAddModal"
          >
            Pridať novú kategóriu
          </button>
        </div>
      </template>
      <div class="max-w-full overflow-x-auto min-w-0">
        <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
          <TableHeader
            class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
          >
            <TableRow>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer" @click="sortBy('id')">
                ID
                <span v-if="sort.key === 'id'">{{ sort.asc ? '▲' : '▼' }}</span>
              </TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer" @click="sortBy('title')">
                Názov
                <span v-if="sort.key === 'title'">{{ sort.asc ? '▲' : '▼' }}</span>
              </TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer" @click="sortBy('parent_id')">
                Nadradená kategória
                <span v-if="sort.key === 'parent_id'">{{ sort.asc ? '▲' : '▼' }}</span>
              </TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Akcie</TableCell>
            </TableRow>
          </TableHeader>
          <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
            <TableEmptyState
              v-if="sortedCategories.length === 0"
              title="Žiadne kategórie"
              hint="Skúste upraviť hľadanie."
              colspan="4"
            />
            <TableRow v-for="cat in sortedCategories" :key="cat.id" class="hover:bg-gray-50 dark:hover:bg-white/5">
          <TableCell class="px-4 py-3 text-sm text-gray-800 dark:text-white/90">{{ cat.id }}</TableCell>
          <TableCell class="px-4 py-3 text-sm text-gray-800 dark:text-white/90">{{ cat.title }}</TableCell>
          <TableCell class="px-4 py-3 text-sm text-gray-800 dark:text-white/90">{{ getParentTitle(cat.parent_id ?? null) }}</TableCell>
          <TableCell class="px-4 py-3 text-sm text-gray-800 dark:text-white/90">
            <button class="mr-2 px-2 py-1 bg-yellow-400 rounded-sm"
                @click="openEditModal(cat)">Upraviť</button>
            <button class="px-2 py-1 bg-red-500 text-white rounded-sm"
                @click="deleteCategory(cat.id)">Vymazať</button>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
</TableCard>

    <div v-if="showModal" class="fixed inset-0 bg-black/30 flex items-center justify-center z-50 dark:bg-white/3">
      <div class="bg-white p-6 rounded-sm shadow-lg w-full max-w-md dark:bg-white/3">
        <h2 class="text-xl font-bold mb-4">{{ editMode ? 'Upraviť' : 'Pridať' }} kategóriu</h2>
        <form @submit.prevent="editMode ? updateCategory() : addCategory()">
          <div class="mb-2">
            <label class="block mb-1">Názov</label>
            <FormKit type="text" v-model="form.title" name="title" :classes="{ input: 'w-full border rounded-sm px-2 py-1' }" validation="required" />
          </div>
          <div class="mb-2">
            <label class="block mb-1">Nadradená kategória</label>
            <FormKit type="select" v-model="form.parent_id" name="parent_id" :classes="{ input: 'w-full border rounded-sm px-2 py-1' }">
              <option :value="null">Žiadna</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                {{ cat.title }}
              </option>
            </FormKit>
          </div>
          <div class="flex justify-end gap-2 mt-4">
            <button type="button" class="px-4 py-2 bg-gray-300 rounded-sm dark:bg-gray-700" @click="closeModal">Zrušiť</button>
            <button type="submit" class="px-4 py-2 bg-brand-600 text-white rounded-sm">Uložiť</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
